import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Sparkles, Wand2, MessageSquareQuote, Copy, Loader2, Lock, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { studioTransform, studioReply, getChatFingerprint } from "../lib/api";
import PostKit from "../components/PostKit";
import { debugLog } from "../lib/logger";
import { T } from "@/locales/tr";
import { papicooWhatsApp } from "@/lib/whatsapp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Block({ title, value }) {
  if (!value) return null;
  return (
    <div className="papicoo-card p-6">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display text-2xl text-[color:var(--papicoo-ink)]">{title}</h3>
        <button
          data-testid={`copy-${title.replace(/\s+/g, "-").toLowerCase()}`}
          onClick={() => { navigator.clipboard.writeText(value); toast.success(T("Copié.")); }}
          className="text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-ink)] inline-flex items-center gap-1 text-xs"
        >
          <Copy size={14} /> {T("Copier")}
        </button>
      </div>
      <pre className="mt-3 whitespace-pre-wrap font-sans text-[15px] leading-relaxed text-[color:var(--papicoo-text)]">{value}</pre>
    </div>
  );
}

export default function Studio() {
  const [params] = useSearchParams();
  const fingerprint = getChatFingerprint();
  const [quota, setQuota] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [tab, setTab] = useState("transform"); // transform | reply
  const [content, setContent] = useState("");
  const [mode, setMode] = useState("pro");
  const [result, setResult] = useState("");
  const [loadingT, setLoadingT] = useState(false);

  const [post, setPost] = useState("");
  const [comment, setComment] = useState("");
  const [replyResult, setReplyResult] = useState("");
  const [loadingR, setLoadingR] = useState(false);

  const loadQuota = async () => {
    try {
      const { data } = await axios.get(`${API}/studio/quota`, { params: { fingerprint } });
      setQuota(data);
    } catch (e) { debugLog("studio quota:", e?.message); }
  };

  useEffect(() => { loadQuota(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid || !params.get("paid")) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/studio/checkout/status/${sid}`);
        if (data.credits_added > 0) toast.success(`+${data.credits_added} transformations Studio débloquées 🌹`);
        loadQuota();
      } catch (e) { console.warn("studio status:", e?.message); }
    })();
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const consume = async () => {
    try {
      const { data } = await axios.post(`${API}/studio/consume`, { fingerprint });
      setQuota({ ...quota, used: data.used, credits_left: data.credits_left, can_transform: data.used < 3 || data.credits_left > 0 });
      return true;
    } catch (e) {
      if (e?.response?.status === 402) { toast.error(T("Quota Studio atteint. Débloque un pack.")); loadQuota(); return false; }
      toast.error(T("Erreur quota.")); return false;
    }
  };

  const unlock = async () => {
    setUnlocking(true);
    try {
      const { data } = await axios.post(`${API}/studio/checkout`, { fingerprint, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || T("Échec checkout.")); setUnlocking(false); }
  };

  const transform = async () => {
    if (!content.trim()) { toast.error(T("Colle un contenu à transformer.")); return; }
    if (quota && !quota.can_transform) { toast.error("Quota atteint."); return; }
    const ok = await consume(); if (!ok) return;
    setLoadingT(true); setResult("");
    try {
      const data = await studioTransform(content, mode);
      setResult(data.text);
    } catch { toast.error(T("Échec de la transformation.")); }
    finally { setLoadingT(false); }
  };

  const doReply = async () => {
    if (!comment.trim()) { toast.error(T("Ajoute le commentaire ou message à traiter.")); return; }
    if (quota && !quota.can_transform) { toast.error("Quota atteint."); return; }
    const ok = await consume(); if (!ok) return;
    setLoadingR(true); setReplyResult("");
    try {
      const data = await studioReply(post, comment);
      setReplyResult(data.text);
    } catch { toast.error(T("Échec de la génération.")); }
    finally { setLoadingR(false); }
  };

  return (
    <div data-testid="page-studio" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      {/* === HERO Studio Photo · Monographie noire (aligné papicoo.fr/studio) === */}
      <div className="relative overflow-hidden rounded-3xl mb-12" data-testid="studio-hero">
        <img
          src={`${process.env.PUBLIC_URL || ""}/papicoo-studio-hero.jpg`}
          alt={T("Studio Papicoo — monographie noire")}
          className="w-full h-[420px] sm:h-[520px] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0c]/90 via-[#0b0b0c]/55 to-[#0b0b0c]/20" />
        <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-12">
          <div data-testid="studio-hero-kicker" className="self-start text-[11px] uppercase tracking-[0.3em] text-[color:var(--papicoo-gold)] bg-[#0b0b0c]/75 px-3 py-1.5 mb-4">{T("Studio Papicoo · Paris · Dakar · Le Monde")}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white max-w-3xl leading-[1.05]">
            {T("Portraits, mode, beauté — traités comme une monographie noire.")}
          </h1>
          <p className="text-white/85 mt-4 max-w-xl leading-relaxed">
            {T("De Paris à Dakar, de New York à Tokyo. Chaque capitale impose sa lumière, ses matières, ses silences. Nous les composons.")}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="https://papicoo.fr/studio" target="_blank" rel="noopener" data-testid="studio-book-session"
               className="papicoo-btn-gold inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium">
              {T("Réserver une séance")}
            </a>
            <a href={papicooWhatsApp(T("Salaam Papicoo, je souhaite réserver une séance studio."))} target="_blank" rel="noopener noreferrer" data-testid="studio-whatsapp"
               className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-white/15 backdrop-blur border border-white/30 text-white hover:bg-white/25 transition">
              {T("WhatsApp Papicoo")}
            </a>
          </div>
        </div>
      </div>

      {/* === Services Studio Photo · 4 piliers === */}
      <div className="mb-14">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Services Studio")}</div>
        <h2 className="font-display text-3xl sm:text-4xl text-[color:var(--papicoo-ink)] mt-2">{T("Quatre disciplines, une seule signature.")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {[
            { title: "Portrait", desc: "Le visage comme paysage — direction de pose, lumière dorée, monographie noire.", cta: "Portrait IA présidentiable", href: "/portrait" },
            { title: "Mode", desc: "Le geste couture — studio + extérieur, couture luxe, identité éditoriale.", cta: "Brief mode", href: "https://wa.me/33753827747" },
            { title: "Beauté", desc: "La peau comme matière — beauty, fini studio, ton chair calibré par capitale.", cta: "Retouche pro", href: "/retouche" },
            { title: "Presets Monographie", desc: "24 packs Lightroom / Camera Raw / Capture One signés Papicoo. Code PAPICOO -20%.", cta: "Voir les presets", href: "https://papicoo.fr/presets" },
          ].map((s) => (
            <a key={s.title} href={s.href} target={s.href.startsWith("http") ? "_blank" : undefined} rel="noopener"
               data-testid={`studio-service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
               className="papicoo-card p-5 hover:-translate-y-1 transition-transform duration-500 block">
              <h3 className="font-display text-xl text-[color:var(--papicoo-ink)]">{T(s.title)}</h3>
              <p className="text-sm text-[color:var(--papicoo-muted)] mt-2 leading-relaxed">{T(s.desc)}</p>
              <div className="mt-4 text-xs uppercase tracking-[0.18em] text-[color:var(--papicoo-gold)]">{T(s.cta)} →</div>
            </a>
          ))}
        </div>
      </div>

      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Studio de contenu IA")}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2">{T("Le verbe. Le rythme. La preuve.")}</h1>
      <p className="text-[color:var(--papicoo-muted)] mt-3 max-w-2xl leading-relaxed">
        {T("Colle un post, une bio, un message. Papicoo le réécrit en version")} <strong>{T("professionnalisée")}</strong> {T("ou en")} <strong>{T("éveil des consciences")}</strong>{T(", avec versets, science, philosophie et culture.")}
      </p>

      {/* Quota studio + paywall */}
      {quota && (
        <div data-testid="studio-quota" className={`mt-6 papicoo-card p-4 flex flex-wrap items-center gap-3 ${quota.can_transform ? "bg-white/60" : "bg-amber-50 border-l-4 border-amber-500"}`}>
          <div className="text-sm text-[color:var(--papicoo-ink)] flex-1">
            {quota.credits_left > 0 ? (
              <>✨ <strong>{quota.credits_left}</strong> {T("transformations restantes (pack débloqué).")}</>
            ) : quota.used < quota.free_limit ? (
              <>🎁 <strong>{quota.free_limit - quota.used}/{quota.free_limit}</strong> {T("transformations gratuites restantes.")}</>
            ) : (
              <span className="inline-flex items-center gap-2"><Lock size={14} /> {T("Tu as utilisé tes 3 transformations gratuites.")}</span>
            )}
          </div>
          {!quota.can_transform || (quota.credits_left === 0 && quota.used >= quota.free_limit - 1) ? (
            <button data-testid="studio-unlock" onClick={unlock} disabled={unlocking} className="inline-flex items-center gap-2 px-4 py-2 rounded-full papicoo-btn-gold text-sm disabled:opacity-60">
              {unlocking ? <Loader2 className="animate-spin" size={14} /> : <ShoppingBag size={14} />}
              {unlocking ? "Redirection…" : T("Débloquer 20 transformations · 9€")}
            </button>
          ) : null}
        </div>
      )}

      <div className="mt-8 flex gap-2">
        <button data-testid="tab-transform" onClick={() => setTab("transform")} className={`px-4 py-2 rounded-full text-sm ${tab === "transform" ? "papicoo-btn-primary" : "bg-white border border-[color:var(--papicoo-border)] text-[color:var(--papicoo-ink)]"}`}>
          <Wand2 size={14} className="inline mr-1" /> {T("Transformer un contenu")}
        </button>
        <button data-testid="tab-reply" onClick={() => setTab("reply")} className={`px-4 py-2 rounded-full text-sm ${tab === "reply" ? "papicoo-btn-primary" : "bg-white border border-[color:var(--papicoo-border)] text-[color:var(--papicoo-ink)]"}`}>
          <MessageSquareQuote size={14} className="inline mr-1" /> {T("Répondre à un commentaire / DM")}
        </button>
      </div>

      {tab === "transform" && (
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <div className="papicoo-card p-6">
            <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Ton contenu")}</label>
            <textarea
              data-testid="studio-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={10}
              className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-4 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
              placeholder={T("Colle ici un post Facebook, une bio, une idée brute…")}
            />
            <div className="mt-4 flex flex-wrap gap-2">
              <button data-testid="mode-pro" onClick={() => setMode("pro")} className={`px-3 py-2 text-xs rounded-full ${mode === "pro" ? "papicoo-btn-gold" : "bg-white border border-[color:var(--papicoo-border)]"}`}>
                {T("Version professionnalisée")}
              </button>
              <button data-testid="mode-eveil" onClick={() => setMode("eveil")} className={`px-3 py-2 text-xs rounded-full ${mode === "eveil" ? "papicoo-btn-gold" : "bg-white border border-[color:var(--papicoo-border)]"}`}>
                {T("Version éveil des consciences")}
              </button>
            </div>
            <button data-testid="studio-transform-btn" onClick={transform} disabled={loadingT} className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full papicoo-btn-primary disabled:opacity-50">
              {loadingT ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Transformer
            </button>
          </div>

          <div data-testid="studio-result-container">
            {result ? <><Block title={T("Résultat")} value={result} /><PostKit text={result} title={T("Publie avec la photo officielle de Papicoo")} /></> : (
              <div className="papicoo-card p-6 text-sm text-[color:var(--papicoo-muted)] italic">
                {T("Le résultat s'affichera ici. Force tranquille — ça vient.")}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "reply" && (
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <div className="papicoo-card p-6 space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Contexte du post (optionnel)")}</label>
              <textarea
                data-testid="reply-post"
                value={post}
                onChange={(e) => setPost(e.target.value)}
                rows={4}
                className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-4 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
                placeholder={T("De quoi parle ton post original ?")}
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Commentaire / DM reçu")}</label>
              <textarea
                data-testid="reply-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={6}
                className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-4 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
                placeholder={T("Colle ici le message à traiter.")}
              />
            </div>
            <button data-testid="reply-generate-btn" onClick={doReply} disabled={loadingR} className="inline-flex items-center gap-2 px-5 py-3 rounded-full papicoo-btn-primary disabled:opacity-50">
              {loadingR ? <Loader2 size={16} className="animate-spin" /> : <MessageSquareQuote size={16} />} Générer 3 réponses
            </button>
          </div>
          <div>
            {replyResult ? <><Block title={T("Trois réponses")} value={replyResult} /><PostKit text={replyResult} title={T("Joins la photo officielle Papicoo à ta réponse")} /></> : (
              <div className="papicoo-card p-6 text-sm text-[color:var(--papicoo-muted)] italic">
                {T("Tu recevras trois variantes : chaleureuse, présidentielle, spirituelle.")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
